import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Stack, Typography, Paper, Grid, LinearProgress, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Button, Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const API = process.env.REACT_APP_API_URL;

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
}

// Semantic colours, brightened for the dark surface, with translucent washes
// rather than solid tints so cells sit on the theme's paper rather than
// punching light holes in it.
const GREEN = '#66bb6a';
const RED = '#ef5350';
const AMBER = '#ffa726';
const WASH_GREEN = 'rgba(102, 187, 106, 0.12)';
const WASH_RED = 'rgba(239, 83, 80, 0.12)';

const pct = (n, d) => (d ? (100 * n) / d : 0);
const f1 = (v) => `${v.toFixed(1)}%`;

export default function ProvenanceStats() {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/provenance/stats`, { headers: authHeaders() });
        if (!res.ok) throw new Error(`Stats unavailable (HTTP ${res.status})`);
        const j = await res.json();
        if (!cancelled) setD(j);
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const report = useMemo(() => {
    if (!d) return '';
    const m = d.matrix, q = d.queue_by_label, a = d.answered_by_label;
    const L = [];
    L.push('VERIFIED / AI-INFERRED MECHANISM VALIDATION — COMNET-D-26-05631');
    L.push('R1-18 (hallucination vs valid inference) and R2 area 7 (validate the mechanism).');
    L.push('');
    L.push(`Tagged findings: ${d.queue_total}  (${q.verified} Verified, ${q.ai_inferred} AI-inferred)`);
    L.push(`Classified     : ${d.answered}  (${a.verified} of ${q.verified} Verified, ${a.ai_inferred} of ${q.ai_inferred} AI-inferred)`);
    L.push('');
    L.push('Does the configuration support the finding?');
    L.push('                      supported   unsupported');
    L.push(`  Verified            ${String(m.verified.sound).padStart(9)}   ${String(m.verified.fabricated).padStart(11)}`);
    L.push(`  AI-inferred         ${String(m.ai_inferred.sound).padStart(9)}   ${String(m.ai_inferred.fabricated).padStart(11)}`);
    L.push('');
    if (d.rates.verified_unsupported_pct != null)
      L.push(`Vouched for but unsupported : ${f1(d.rates.verified_unsupported_pct)} of Verified findings`);
    if (d.rates.ai_inferred_sound_pct != null)
      L.push(`Hedged on but sound         : ${f1(d.rates.ai_inferred_sound_pct)} of AI-inferred findings`);
    if (d.label_shares?.verified_pct != null)
      L.push(`Line-matched / AI-inferred  : ${f1(d.label_shares.verified_pct)} / ${f1(d.label_shares.ai_inferred_pct)}  (${q.verified} and ${q.ai_inferred} of ${d.queue_total})`);
    L.push(`Unsupported, of those judged: ${d.overall.unsupported}/${d.answered} (${f1(d.overall.unsupported_pct ?? 0)})`);
    if (d.answered < d.queue_total)
      L.push(`\nPROVISIONAL — ${d.queue_total - d.answered} of ${d.queue_total} not yet classified.`);
    return L.join('\n');
  }, [d]);

  if (error) return <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}><Alert severity="error">{error}</Alert></Box>;
  if (!d) return <Box sx={{ p: 4 }}><LinearProgress /></Box>;

  const m = d.matrix, q = d.queue_by_label, a = d.answered_by_label;
  const complete = d.answered >= d.queue_total;
  // `tint` shades the cell, `ink` carries the column's meaning on the figure
  // itself -- the tints are pale enough that colour alone would not read.
  const cell = (n, den, tint, ink) => (
    <TableCell align="center" sx={{ bgcolor: tint }}>
      <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: den ? ink : 'text.disabled', lineHeight: 1.2 }}>
        {den ? n : '—'}
      </Typography>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: den ? ink : 'text.disabled', opacity: 0.85 }}>
        {den ? f1(pct(n, den)) : 'not yet judged'}
      </Typography>
    </TableCell>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Verified / AI-Inferred Validation</Typography>
          <Typography variant="body2" color="text.secondary">
            Reviewer 1 comment 18 and Reviewer 2 area 7. One question was asked of every tagged
            finding — does the configuration support it? — so the answer can be crossed against
            the label the system applied.
          </Typography>
        </Box>
        <Tooltip arrow title="Copy the figures as text">
          <span>
            <Button variant="outlined" size="small" color={copied ? 'success' : 'primary'}
              startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
              onClick={async () => { try { await navigator.clipboard.writeText(report); setCopied(true); } catch { setCopied(false); } }}
              disabled={!d.answered} sx={{ textTransform: 'none', minWidth: 132 }}>
              {copied ? 'Copied' : 'Copy figures'}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {!complete && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {d.answered} of {d.queue_total} classified — {a.verified} of {q.verified} Verified and{' '}
          {a.ai_inferred} of {q.ai_inferred} AI-inferred. Figures are provisional; the
          Verified row cannot be read until those findings are judged.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main' }}>
          Does the configuration support the finding?
        </Typography>
        <TableContainer sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>NSCAT said</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: GREEN, bgcolor: WASH_GREEN }}>
                  Supported
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: RED, bgcolor: WASH_RED }}>
                  Unsupported
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Judged</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: GREEN }}>Verified — cites a line</TableCell>
                {cell(m.verified.sound, a.verified, WASH_GREEN, GREEN)}
                {cell(m.verified.fabricated, a.verified, WASH_RED, RED)}
                <TableCell align="center">{a.verified} / {q.verified}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: AMBER }}>AI-inferred — no line</TableCell>
                {cell(m.ai_inferred.sound, a.ai_inferred, WASH_GREEN, GREEN)}
                {cell(m.ai_inferred.fabricated, a.ai_inferred, WASH_RED, RED)}
                <TableCell align="center">{a.ai_inferred} / {q.ai_inferred}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          Percentages are of the findings judged in that row.
        </Typography>
      </Paper>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${RED}` }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: RED }}>
              {d.rates.verified_unsupported_pct != null ? f1(d.rates.verified_unsupported_pct) : '—'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Vouched for, yet unsupported</Typography>
            <Typography sx={{ fontSize: '0.68rem', opacity: 0.75 }}>
              The tag over-claimed: it pointed at a line that does not bear the finding out
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${GREEN}` }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: GREEN }}>
              {d.rates.ai_inferred_sound_pct != null ? f1(d.rates.ai_inferred_sound_pct) : '—'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Hedged on, yet sound</Typography>
            <Typography sx={{ fontSize: '0.68rem', opacity: 0.75 }}>
              The tag was cautious: unanchored, but the configuration supports it
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderTop: '3px solid #1976d2' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
              {d.label_shares?.verified_pct != null
                ? `${f1(d.label_shares.verified_pct)} / ${f1(d.label_shares.ai_inferred_pct)}`
                : '\u2014'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Line-matched / AI-inferred</Typography>
            <Typography sx={{ fontSize: '0.68rem', opacity: 0.75 }}>
              What the tag did before any human judgement: {q.verified} of {d.queue_total} findings
              carried a matching configuration line, {q.ai_inferred} did not
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderTop: '3px solid #78909c' }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {d.overall.unsupported_pct != null ? f1(d.overall.unsupported_pct) : '—'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Unsupported, of those judged
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', opacity: 0.75 }}>
              {d.overall.unsupported} of {d.answered} judged
              {d.answered < d.queue_total
                ? ` \u2014 not yet a rate over all ${d.queue_total}`
                : ` of ${d.queue_total} tagged findings`}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main' }}>For the manuscript</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          The two off-diagonal cells are the mechanism's two error directions, and neither can be
          read from the AI-inferred subset alone. <b>Vouched for yet unsupported</b> is the more
          serious: the tag tells an auditor a finding is evidenced when it is not. <b>Hedged on
          yet sound</b> is the cost of caution — work the tag sends for review that did not need it.
        </Typography>
      </Paper>
    </Box>
  );
}
